import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Spin,
  Table,
  Image,
  Row,
  Col,
  Divider,
  message,
  Button,
} from "antd";
import axios from "axios";
import { CaretLeftOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive"; // <-- Add this import

const { Title, Text } = Typography;

const OrderDetail = () => {
  const { OrderID } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery({ maxWidth: 767 }); // <-- Detect mobile

  useEffect(() => {
    checkUserActiveStatus();
    fetchOrderDetail();
    // eslint-disable-next-line
  }, [OrderID]);

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          // Redirect to home if AllowtoOrder is false
          if (response.data.AllowtoOrder === false) {
            window.location.href = "/";
            return;
          }

          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === true
          ) {
            alert("Your session has expired. Please log in again.");

            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.reload();
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const fetchOrderDetail = async () => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.get(
        global.config.APIURL.BaseURL +
          "api/Order/GetOrderDetailByID?OrderID=" +
          OrderID,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        setOrder(response.data[0]);
      } else {
        setOrder(null);
      }
    } catch (error) {
      message.error("Failed to fetch order detail.");
      setOrder(null);
    }
    setLoading(false);
  };

  // Helper for Indian number format with 2 decimals
  const formatINR = (value) =>
    value !== undefined && value !== null
      ? value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  // Helper for date format "dd-MM-yyyy"
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Responsive columns for mobile and desktop
  const columns = isMobile
    ? [
        {
          title: "Product",
          dataIndex: "ProductID",
          key: "ProductID",
          render: (_, record) => (
            <div style={{ display: "flex", gap: 8 }}>
              <Image
                src={record.ImageUrl}
                alt={record.ProductName}
                width={50}
                height={50}
                style={{ objectFit: "cover", borderRadius: 6 }}
                fallback="https://via.placeholder.com/50"
              />
              <div>
                <div>
                  <b>{record.ProductName}</b>
                </div>
                <div>
                  <b>Design:</b> {record.DesignNo}
                </div>
                <div>
                  <b>Color:</b> {record.Color} <b>Size:</b> {record.Size}
                </div>
                <div>
                  <b>Box:</b> {record.NoOfBox} <b>Box Size:</b> {record.BoxSize}
                </div>
                <div>
                  <b>Qty:</b> {record.TotalQuantity}
                </div>
                <div>
                  <b>Price:</b> ₹{formatINR(record.Price)}
                </div>
                <div>
                  <b>Amount:</b>{" "}
                  <span style={{ color: "#1890ff" }}>
                    ₹{formatINR(record.Amount)}
                  </span>
                </div>
                {record.Specification && (
                  <div>
                    <b>Spec:</b> {record.Specification}
                  </div>
                )}
              </div>
            </div>
          ),
        },
      ]
    : [
        {
          title: "Image",
          dataIndex: "ImageUrl",
          key: "ImageUrl",
          render: (url) =>
            url ? (
              <Image
                src={url}
                alt="Product"
                width={60}
                height={60}
                style={{ objectFit: "cover", borderRadius: 8 }}
              />
            ) : (
              <Image
                src="https://via.placeholder.com/60"
                width={60}
                height={60}
              />
            ),
        },
        {
          title: "Product Name",
          dataIndex: "ProductName",
          key: "ProductName",
        },
        {
          title: "Design No",
          dataIndex: "DesignNo",
          key: "DesignNo",
        },
        {
          title: "Color",
          dataIndex: "Color",
          key: "Color",
        },
        {
          title: "Size",
          dataIndex: "Size",
          key: "Size",
        },
        {
          title: "Box",
          dataIndex: "NoOfBox",
          key: "NoOfBox",
        },
        {
          title: "Box Size",
          dataIndex: "BoxSize",
          key: "BoxSize",
        },
        {
          title: "Total Qty",
          dataIndex: "TotalQuantity",
          key: "TotalQuantity",
        },
        {
          title: "Price",
          dataIndex: "Price",
          key: "Price",
          render: (value) => `₹${formatINR(value)}`,
        },
        {
          title: "Amount",
          dataIndex: "Amount",
          key: "Amount",
          render: (value) => <b>₹{formatINR(value)}</b>,
        },
        {
          title: "Specification",
          dataIndex: "Specification",
          key: "Specification",
        },
      ];

  return (
    <div style={{ padding: 0, margin: 0 }}>
      <Card style={{ padding: 0, margin: 0 }}>
        <div
          style={{
            maxWidth: 1100,
            margin: isMobile ? 0 : "auto",
            padding: isMobile ? 8 : 16,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Button
              type={isMobile ? "default" : "primary"}
              icon={<CaretLeftOutlined />}
              onClick={() => navigate("/myorders")}
              size={isMobile ? "small" : "middle"}
            >
              Back to Order List
            </Button>
          </div>
          <Title
            level={2}
            style={{
              marginBottom: 16,
              fontSize: isMobile ? 20 : 28,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            Order Detail
          </Title>
          {loading ? (
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <Spin size="large" />
            </div>
          ) : order ? (
            <>
              <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Text strong>Order Date: </Text>
                    <Text>{formatDate(order.OrderDate)}</Text>
                    <br />
                    <Text strong>Total Box: </Text>
                    <Text>{order.TotalBox}</Text>
                    <br />
                    <Text strong>Total Quantity: </Text>
                    <Text>{order.TotalQuantity}</Text>
                  </Col>
                  <Col xs={24} md={12}>
                    <Text strong>Sub Amount: </Text>
                    <Text>₹{formatINR(order.SubAmount)}</Text>
                    <br />
                    <Text strong>GST Amount: </Text>
                    <Text>₹{formatINR(order.GSTAmount)}</Text>
                    <br />
                    <Text strong>Grand Total: </Text>
                    <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                      ₹{formatINR(order.GrandTotal)}
                    </Text>
                  </Col>
                </Row>
              </Card>
              <Divider orientation="center">Order Items</Divider>
              <Table
                columns={columns}
                dataSource={order.OrderDetail}
                rowKey={(record) =>
                  `${record.ProductID}-${record.DesignNo}-${record.Specification}`
                }
                pagination={false}
                bordered
                size={isMobile ? "small" : "middle"}
                scroll={isMobile ? { x: true } : undefined}
              />
            </>
          ) : (
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <Text type="danger">Order not found.</Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OrderDetail;
